/**
 * BASE: WhisperX — one local model transcribes AND aligns.
 *
 * The alternative to `baseGemini`, and an honest A/B against it: everything
 * downstream (tiling, the seam seal, `tilesToSegments`) is byte-for-byte the
 * same code, so a difference in the output is a difference in the engine.
 *
 * WhisperX returns 0..1 confidences; they are re-mapped onto the log-prob-ish
 * scale `aligner.confidenceFromScore` expects so both bases grade the same way.
 *
 * The `./whisperxClient` import stays DYNAMIC on purpose: the helper is a
 * developer-machine tool, and phones must never pay to download its client.
 */

import { cleanStoredLabel, isFillerSpelling } from '../../lib/labelText';
import type { AlignedWord } from '../aligner';
import type { VerbatimToken } from '../gemini';
import { tileFromWords, type AlignedTilesResult, type BuildTilesArgs } from './alignedTiling';

export interface BaseWhisperxOptions {
  /** Ask WhisperX for hesitations it would otherwise tidy away. */
  biasFillers: boolean;
}

export async function baseWhisperx(
  args: BuildTilesArgs,
  options: BaseWhisperxOptions,
): Promise<AlignedTilesResult> {
  const { uploadWav, signal, onPhase } = args;
  const { biasFillers } = options;

  onPhase?.(biasFillers ? 'WhisperX (filler-biased)…' : 'WhisperX: transcribing and aligning…', 0.1);
  const { transcribeWithWhisperx } = await import('../whisperxClient');
  const wx = await transcribeWithWhisperx({
    wav: uploadWav,
    fillerBias: biasFillers,
    signal,
  });
  if (wx.words.length === 0) throw new Error('WhisperX returned no words.');
  const tokens: VerbatimToken[] = wx.words.map((w) => ({
    text: cleanStoredLabel(w.text) || w.text,
    kind: isFillerSpelling(w.text) ? 'filler' : 'word',
  }));
  const measured: AlignedWord[] = wx.words.map((w) => ({
    start: w.start,
    end: w.end,
    text: w.text,
    // WhisperX scores are 0..1 confidence; map onto the log-prob-ish scale
    // `confidenceFromScore` expects so both engines grade the same way.
    score: (w.score || 0.5) > 0.7 ? 0 : (w.score || 0.5) > 0.4 ? -2 : -6,
  }));
  onPhase?.('Building the labeled timeline…', 0.7);
  return tileFromWords(args, tokens, measured, [], wx.language);
}
